// FeedbackPage.js
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Container,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import api from '../api/axios';

const FeedbackPage = () => {
  const [formData, setFormData] = useState({
    reddit_username: '',
    message: ''
  });
  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: false,
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      setStatus({
        loading: false,
        success: false,
        error: true,
        message: 'Please enter a message'
      });
      return;
    }
    
    setStatus({ ...status, loading: true });
    
    try {
      const response = await api.post('/api/feedback/', formData);
      setStatus({
        loading: false,
        success: true,
        error: false,
        message: response.data.message
      });
      // Reset form
      setFormData({
        reddit_username: '',
        message: ''
      });
    } catch (error) {
      setStatus({
        loading: false,
        success: false,
        error: true,
        message: error.response?.data?.message || 'Something went wrong. Please try again.'
      });
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          Feedback
        </Typography>
        <Typography variant="subtitle1" align="center" sx={{ mb: 4 }}>
          We'd love to hear your thoughts on Fallon d'Floor. Your feedback helps us improve!
        </Typography>
        
        <Paper sx={{ 
          p: 4, 
          bgcolor: 'rgba(255,255,255,0.05)',
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Reddit Username (optional)"
              name="reddit_username"
              value={formData.reddit_username}
              onChange={handleChange}
              fullWidth
              margin="normal"
              variant="outlined"
              sx={{ mb: 3 }}
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label="Your Feedback"
              name="message"
              value={formData.message}
              onChange={handleChange}
              fullWidth
              required
              multiline
              rows={6}
              margin="normal"
              variant="outlined"
              placeholder="Share your thoughts, suggestions, or report issues..."
              sx={{ mb: 3 }}
              InputLabelProps={{ shrink: true }}
            />
            
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              size="large"
              disabled={status.loading}
              endIcon={status.loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {status.loading ? 'Sending...' : 'Submit Feedback'}
            </Button>
          </Box>
        </Paper>
        
        {/* Info section */}
        <Paper sx={{ 
          p: 3, 
          mt: 4, 
          bgcolor: 'rgba(0,0,0,0.3)',
          borderRadius: 2
        }}>
          <Typography variant="body2" gutterBottom>
            Your feedback is important to us and will be reviewed by our team.
          </Typography>
          <Typography variant="body2">
            We may use your feedback to improve the Fallon d'Floor experience, but we won't share your Reddit username publicly.
          </Typography>
        </Paper>
      </Box>
      
      {/* Success/Error notification */}
      <Snackbar 
        open={status.success || status.error} 
        autoHideDuration={6000} 
        onClose={() => setStatus({...status, success: false, error: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setStatus({...status, success: false, error: false})} 
          severity={status.success ? "success" : "error"}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {status.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FeedbackPage;